import { useState } from "react";
import { toast } from "sonner";
import { api, inr, formatApiError } from "../lib/api";
import { PAYMENT_MODES } from "../data/content";
import { PlayerPicker } from "./PlayerPicker";
import { AllocationPreview } from "./AllocationPreview";

const inputCls = "bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#D4AF37] w-full";
const labelCls = "block text-[10px] uppercase tracking-widest text-white/40 mb-1.5";
const todayIST = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

// Record (no `existing`) or fully edit (`existing` allocated payment) a single-amount payment.
export const AllocatedPaymentForm = ({ players, existing, onSaved, onCancel }) => {
  const blank = { player_id: "", date: todayIST(), amount: "", mode: "UPI", notes: "" };
  const [form, setForm] = useState(existing
    ? { player_id: existing.player_id, date: existing.date, amount: String(existing.amount_received ?? 0), mode: existing.mode || "UPI", notes: existing.notes || "" }
    : blank);
  const [useCredit, setUseCredit] = useState(true);
  const [manual, setManual] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.player_id) { toast.error("Pick a player from the list"); return; }
    if (!preview) { toast.error("Fix the allocation before saving"); return; }
    if (existing && !window.confirm(`Save changes? Amount ${inr(existing.amount_received)} → ${inr(Number(form.amount) || 0)}. Balance after: ${inr(preview.balance_after)}, credit balance after: ${inr(preview.credit_after)}.`)) return;
    setSaving(true);
    const body = { ...form, amount: Number(form.amount) || 0, use_credit: useCredit,
      allocations: manual ? preview.rows.filter((r) => r.allocated > 0).map((r) => ({ month: r.month, category: r.category, amount: r.allocated })) : null };
    try {
      const { data } = existing
        ? await api.put(`/admin/payments/${existing.id}/allocated`, body)
        : await api.post("/admin/payments/allocated", body);
      toast.success(existing ? "Payment updated" : "Payment recorded");
      if (!existing) { setForm({ ...blank, date: form.date, mode: form.mode }); setManual(null); setUseCredit(true); }
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const disabled = saving || !preview || !!preview.warning || (!(Number(form.amount) > 0) && !(preview.credit_used > 0));
  return (
    <form onSubmit={submit} data-testid={existing ? "edit-alloc-form" : "payment-form"}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {existing
          ? <div className="lg:col-span-2"><label className={labelCls}>Player</label><p className="text-sm py-2.5">{existing.player_name}</p></div>
          : <div className="lg:col-span-2"><label className={labelCls}>Player *</label><PlayerPicker players={players} value={form.player_id} onChange={(v) => { setForm({ ...form, player_id: v }); setManual(null); }} /></div>}
        <div><label className={labelCls}>Payment Date *</label><input required type="date" value={form.date} onChange={set("date")} className={inputCls} data-testid="pay-date" /></div>
        <div><label className={labelCls}>Total Amount Received ₹</label><input type="number" min="0" step="any" value={form.amount} onChange={set("amount")} placeholder="0" className={inputCls} data-testid="pay-amount" /></div>
        <div><label className={labelCls}>Payment Method</label>
          <select value={form.mode} onChange={set("mode")} className={inputCls} data-testid="pay-mode">
            {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="sm:col-span-1 lg:col-span-3"><label className={labelCls}>Notes</label><input value={form.notes} onChange={set("notes")} className={inputCls} data-testid="pay-notes" /></div>
      </div>
      <AllocationPreview playerId={form.player_id} date={form.date} amount={form.amount} useCredit={useCredit} setUseCredit={setUseCredit}
        manual={manual} setManual={setManual} excludeId={existing?.id} onPreview={setPreview} />
      <div className="flex gap-3 mt-5">
        <button type="submit" disabled={disabled} className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-8 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200 disabled:opacity-40" data-testid={existing ? "edit-alloc-save" : "pay-submit"}>
          {saving ? "Saving…" : existing ? "Save Changes" : (Number(form.amount) > 0 ? "Record Payment" : "Apply Credit")}
        </button>
        {onCancel && <button type="button" onClick={onCancel} className="border border-white/20 text-white/70 text-xs uppercase tracking-widest px-6 py-3 rounded-sm hover:border-[#D4AF37]">Cancel</button>}
      </div>
      {existing?.history?.length > 0 && (
        <div className="mt-6 text-xs text-white/50 border-t border-white/10 pt-4" data-testid="edit-alloc-history">
          <p className="uppercase tracking-widest text-[10px] text-white/40 mb-2">Edit history</p>
          {existing.history.map((h) => (
            <p key={h.edited_at} className="mb-1">{new Date(h.edited_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} · {h.edited_by} · was {inr(h.previous.amount_received)} on {h.previous.date} ({h.previous.mode})</p>
          ))}
        </div>
      )}
    </form>
  );
};
